/**
 * Difficulty — the four risk tiers and their multiplier ladders.
 *
 * Model (matches the real InOut "Chicken Road" published numbers):
 * the road has N = 25 potential columns and K of them hide a deadly car,
 * drawn without replacement — a "mines"-style hypergeometric setup.
 *
 *   P(survive k lanes)          = C(N-K, k) / C(N, k)
 *   M[k]                        = RTP / P(survive k)
 *   P(die at lane k | alive)    = K / (N - k + 1)     ← rises as you advance
 *
 * A tier's road length is exactly N - K lanes (once you've passed every safe
 * column there is nothing left to cross). With RTP = 0.98 this reproduces the
 * real game's max multipliers: Easy 24.50x, Medium 2 254x, Hard 52 067.39x,
 * Hardcore 3 203 384.80x.
 */

export const RTP = 0.98;
export const TOTAL_COLUMNS = 25;

const binom = (n, k) => {
  if (k < 0 || k > n) return 0;
  let r = 1;
  for (let i = 1; i <= k; i++) r = (r * (n - i + 1)) / i;
  return r;
};

function buildLadder(K) {
  const lanes = TOTAL_COLUMNS - K;
  // ladder[0] = 1 (at the sidewalk, no winnings yet); ladder[k] for k lanes passed.
  const ladder = [1];
  for (let k = 1; k <= lanes; k++) {
    const pSurvive = binom(TOTAL_COLUMNS - K, k) / binom(TOTAL_COLUMNS, k);
    ladder.push(Math.round((RTP / pSurvive) * 100) / 100);
  }
  return ladder;
}

const TIERS = [
  { id: 'easy',     label: 'Easy',     K: 1  },
  { id: 'medium',   label: 'Medium',   K: 3  },
  { id: 'hard',     label: 'Hard',     K: 5  },
  { id: 'hardcore', label: 'Hardcore', K: 10 },
].map((t) => ({
  ...t,
  lanes: TOTAL_COLUMNS - t.K,
  ladder: buildLadder(t.K),
  odds: `${t.K} out of ${TOTAL_COLUMNS}`,
  // Conditional death probability when stepping onto lane k (1-based).
  stepDeathProb: (k) => t.K / (TOTAL_COLUMNS - k + 1),
}));

export const DIFFICULTIES = TIERS;

export function getDifficulty(id) {
  return TIERS.find((t) => t.id === id) || TIERS[0];
}

/** Compact display for huge multipliers: 1.63x · 52 067x · 3.20M x */
export function formatMultiplier(m) {
  if (m >= 1_000_000) return `${(m / 1_000_000).toFixed(2)}M`;
  if (m >= 10_000) return groupThousands(Math.round(m));
  if (m >= 100) return `${Math.round(m * 10) / 10}`;
  return m.toFixed(2);
}

/** 3203384 → "3 203 384" (space thousands separator, as in the real UI). */
export function groupThousands(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
